import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useCms } from '../context/CmsContext'

export default function Header() {
  const { siteSettings, tattooStyles } = useCms()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()
  const dropdownRef = useRef(null)

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false)
    setMobileSubmenuOpen(false)
    setDropdownOpen(false)
  }, [location.pathname, location.search])

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <header className={`sticky top-0 z-40 transition-all duration-300 ${scrolled ? 'bg-[#0f1013]/95 backdrop-blur-md border-b border-[#282b34] shadow-xl py-3' : 'bg-[#0f1013]/90 border-b border-[#282b34]/60 py-4'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* DESKTOP HEADER (min-width: 1200px) */}
        <div className="hidden min-[1200px]:flex flex-col items-center gap-3">
          
          {/* Top Row: Address Left | Centered Logo | Phone & CTA Right */}
          <div className="w-full flex items-center justify-between">
            {/* Left Info */}
            <div className="w-1/3 flex items-center gap-2 text-xs text-neutral-400">
              <i className="fa-solid fa-location-dot text-[#cbb05e]" aria-hidden="true" />
              <span className="truncate">{siteSettings.address}</span>
            </div>

            {/* Centered Logo */}
            <div className="w-1/3 flex justify-center">
              <Link to="/" className="flex flex-col items-center group text-center" aria-label="Docuit ink forside">
                <div className="relative w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border-2 border-[#cbb05e]/60 p-1 bg-[#17191e] transition-transform duration-300 group-hover:scale-105 shadow-xl">
                  <img
                    src={siteSettings.logo}
                    alt="Docuit ink logo"
                    className="w-full h-full object-contain rounded-full"
                  />
                </div>
              </Link>
            </div>

            {/* Right Actions */}
            <div className="w-1/3 flex items-center justify-end gap-5">
              <a
                href={`tel:${siteSettings.phoneRaw}`}
                className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-200 hover:text-[#cbb05e] transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-[#cbb05e]" aria-hidden="true" />
                <span className="whitespace-nowrap">{siteSettings.phone}</span>
              </a>

              <Link
                to="/booking"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#cbb05e] hover:bg-[#d8be65] text-[#0f1013] text-xs font-bold uppercase tracking-wider rounded-full transition-all shadow-md hover:shadow-lg cursor-pointer whitespace-nowrap"
              >
                <i className="fa-solid fa-calendar-alt text-xs" aria-hidden="true" />
                Book en tid
              </Link>
            </div>
          </div>

          {/* Bottom Row: Centered Navigation Links directly below Logo */}
          <nav className="w-full flex items-center justify-center gap-8 pt-2.5 border-t border-[#282b34]/60">
            <Link
              to="/"
              className={`text-xs uppercase tracking-widest font-semibold transition-colors py-1.5 border-b-2 ${isActive('/') && location.pathname === '/' ? 'text-white border-white font-bold' : 'text-neutral-300 border-transparent hover:text-white'}`}
            >
              Forside
            </Link>

            {/* Tatoveringer Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                onMouseEnter={() => setDropdownOpen(true)}
                className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-semibold transition-colors py-1.5 border-b-2 cursor-pointer ${isActive('/tatoveringer') ? 'text-white border-white font-bold' : 'text-neutral-300 border-transparent hover:text-white'}`}
                aria-expanded={dropdownOpen}
              >
                Tatoveringer
                <i className={`fa-solid fa-chevron-down text-[10px] transition-transform ${dropdownOpen ? 'rotate-180 text-white' : ''}`} aria-hidden="true" />
              </button>

              {dropdownOpen && (
                <div
                  onMouseLeave={() => setDropdownOpen(false)}
                  className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-64 bg-[#17191e] border border-[#282b34] rounded-lg shadow-2xl py-2 z-50 animate-in fade-in duration-150"
                >
                  {tattooStyles.map((style) => (
                    <Link
                      key={style.id}
                      to={`/galleri?stil=${style.id}`}
                      className="block px-4 py-2 text-xs text-neutral-200 hover:text-white hover:bg-neutral-800/80 transition-colors font-medium"
                    >
                      {style.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/galleri"
              className={`text-xs uppercase tracking-widest font-semibold transition-colors py-1.5 border-b-2 ${isActive('/galleri') ? 'text-white border-white font-bold' : 'text-neutral-300 border-transparent hover:text-white'}`}
            >
              Galleri
            </Link>

            <Link
              to="/om-docuit-ink"
              className={`text-xs uppercase tracking-widest font-semibold transition-colors py-1.5 border-b-2 ${isActive('/om-docuit-ink') ? 'text-white border-white font-bold' : 'text-neutral-300 border-transparent hover:text-white'}`}
            >
              Om Docuit ink
            </Link>

            <Link
              to="/booking"
              className={`text-xs uppercase tracking-widest font-semibold transition-colors py-1.5 border-b-2 ${isActive('/booking') ? 'text-white border-white font-bold' : 'text-neutral-300 border-transparent hover:text-white'}`}
            >
              Booking & Kontakt
            </Link>
          </nav>

        </div>

        {/* MOBILE & TABLET HEADER (< 1200px) */}
        <div className="flex min-[1200px]:hidden items-center justify-between">
          
          {/* Brand / Logo */}
          <Link to="/" className="flex items-center gap-3 group" aria-label="Docuit ink forside">
            <div className="relative w-20 h-20 sm:w-22 sm:h-22 rounded-full overflow-hidden border-2 border-[#cbb05e]/60 p-1 bg-[#17191e] transition-transform duration-300 group-hover:scale-105 shadow-md">
              <img
                src={siteSettings.logo}
                alt="Docuit ink logo"
                className="w-full h-full object-contain rounded-full"
              />
            </div>
          </Link>

          {/* Mobile Actions */}
          <div className="flex items-center gap-2">
            <Link
              to="/booking"
              className="inline-flex items-center justify-center px-3.5 py-1.5 bg-[#cbb05e] text-[#0f1013] rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap shadow-sm"
            >
              Book
            </Link>

            {/* Burgermenu Toggle (< 1200px) */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="flex items-center justify-center w-8 h-8 text-white bg-transparent border-none p-0 cursor-pointer focus:outline-none"
              aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
              aria-expanded={mobileMenuOpen}
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden="true" />
            </button>
          </div>

        </div>

      </div>

      {/* Mobile Drawer Menu (<1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-[#17191e] border-b border-[#282b34] px-4 py-6 shadow-2xl animate-in slide-in-from-top-4 duration-200">
          <nav className="flex flex-col gap-4">
            <Link
              to="/"
              className={`text-sm uppercase tracking-wider font-semibold py-2 px-3 rounded transition-colors ${isActive('/') && location.pathname === '/' ? 'text-white font-bold border-l-2 border-white pl-3.5 bg-neutral-800/40' : 'text-neutral-300 hover:text-white hover:bg-neutral-800'}`}
            >
              Forside
            </Link>

            <div className="flex flex-col">
              <div className="flex items-center justify-between">
                <Link
                  to="/tatoveringer"
                  className={`text-sm uppercase tracking-wider font-semibold py-2 px-3 rounded flex-1 transition-colors ${isActive('/tatoveringer') ? 'text-white font-bold border-l-2 border-white pl-3.5 bg-neutral-800/40' : 'text-neutral-300 hover:text-white hover:bg-neutral-800'}`}
                >
                  Tatoveringer
                </Link>
                <button
                  type="button"
                  onClick={() => setMobileSubmenuOpen(!mobileSubmenuOpen)}
                  className="p-2 text-neutral-400 hover:text-white transition-colors"
                  aria-label={mobileSubmenuOpen ? 'Skjul stilarter' : 'Vis stilarter'}
                >
                  <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${mobileSubmenuOpen ? 'rotate-180 text-white' : ''}`} aria-hidden="true" />
                </button>
              </div>

              {mobileSubmenuOpen && (
                <div className="border-l border-neutral-700 pl-4 ml-3 my-1 space-y-1 py-1 animate-in fade-in duration-150">
                  {tattooStyles.map((style) => (
                    <Link
                      key={style.id}
                      to={`/galleri?stil=${style.id}`}
                      className="block text-xs text-neutral-300 hover:text-white py-1.5"
                    >
                      • {style.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/galleri"
              className={`text-sm uppercase tracking-wider font-semibold py-2 px-3 rounded transition-colors ${isActive('/galleri') ? 'text-white font-bold border-l-2 border-white pl-3.5 bg-neutral-800/40' : 'text-neutral-300 hover:text-white hover:bg-neutral-800'}`}
            >
              Galleri
            </Link>

            <Link
              to="/om-docuit-ink"
              className={`text-sm uppercase tracking-wider font-semibold py-2 px-3 rounded transition-colors ${isActive('/om-docuit-ink') ? 'text-white font-bold border-l-2 border-white pl-3.5 bg-neutral-800/40' : 'text-neutral-300 hover:text-white hover:bg-neutral-800'}`}
            >
              Om Docuit ink
            </Link>

            <Link
              to="/booking"
              className={`text-sm uppercase tracking-wider font-semibold py-2 px-3 rounded transition-colors ${isActive('/booking') ? 'text-white font-bold border-l-2 border-white pl-3.5 bg-neutral-800/40' : 'text-neutral-300 hover:text-white hover:bg-neutral-800'}`}
            >
              Booking & Kontakt
            </Link>

            <div className="pt-4 border-t border-neutral-800 flex flex-col gap-3">
              <a
                href={`tel:${siteSettings.phoneRaw}`}
                className="flex items-center gap-3 px-3 py-2 text-neutral-300 hover:text-white text-sm font-medium transition-colors"
              >
                <i className="fa-solid fa-phone text-[#cbb05e]" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring på {siteSettings.phone}</span>
              </a>
              <a
                href={`mailto:${siteSettings.email}`}
                className="flex items-center gap-3 px-3 py-2 text-neutral-300 hover:text-white text-sm font-medium transition-colors"
              >
                <i className="fa-solid fa-envelope text-[#cbb05e]" aria-hidden="true" />
                <span className="whitespace-nowrap">{siteSettings.email}</span>
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}
